import React from 'react';

import { Flex, Tab } from '@opendoor/bricks/core';

import { TabLink, TabLinkProps } from './TabLink';

export type NavItem = {
  title: string;
  name: string;
  href?: string;
  active?: boolean;
  onClick?: () => void;
};

export type GlobalHeaderProps = {
  title:
    | string
    | {
        component: React.ReactNode;
        a11yName: string;
      };
  navItems?: Array<NavItem>;
  rightComponent?: JSX.Element;
  bg?: string;
};

/** Renders a global header, which shows Hub top-level navigation items. */
export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  title,
  bg,
  navItems,
  rightComponent,
}) => {
  if (!navItems && !rightComponent) {
    return null;
  }

  return (
    <Flex alignItems="center" as="header" width="100%" boxSizing="initial">
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        py={['8', null, '9']}
        pb={['2', null, '9']}
        paddingLeft={['7', null, '8']}
        paddingRight="7"
        data-testid="global-variant-responsive"
        boxSizing="initial"
        width="100%"
        bgColor="neutrals0"
        bg={bg}
        minHeight={'40px'}
      >
        {navItems && (
          <Flex
            aria-label={typeof title === 'string' ? title : title.a11yName}
            as="nav"
            flexWrap="nowrap"
            alignItems="center"
            overflow="auto"
            sx={{
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
            data-testid="nav"
          >
            <NavLinks navItems={navItems} tabLinkProps={{ pr: [6, null, null, 4] }} />
          </Flex>
        )}
        {rightComponent}
      </Flex>
    </Flex>
  );
};

type NavLinksProps = Required<Exclude<GlobalHeaderProps['navItems'], undefined>>;
const NavLinks: React.FC<{ navItems: NavLinksProps; tabLinkProps?: Partial<TabLinkProps> }> = ({
  navItems,
  tabLinkProps,
}) => (
  <>
    {navItems.map(({ title, href, onClick, active, name }) =>
      href ? (
        <TabLink
          flex="none"
          width="auto"
          id={name}
          variant="sub-nav"
          isActive={active}
          to={href}
          pl={[0, null, null, 4]}
          pr={[7, null, null, 4]}
          analyticsName={`cake-${name}`}
          aria-label={null}
          key={`navlink-${name}`}
          {...tabLinkProps}
        >
          {title}
        </TabLink>
      ) : (
        <Tab
          flex="none"
          width="auto"
          id={name}
          variant="sub-nav"
          isActive={active}
          onClick={onClick}
          pl={[0, null, null, 4]}
          pr={[7, null, null, 4]}
          analyticsName={`cake-${name}`}
          aria-label={`Go to learn more about ${title}`}
          key={`navlink-${name}`}
        >
          {title}
        </Tab>
      ),
    )}
  </>
);
